// Modbus-Register-Map-Import + Mapping-UI (Issue #156, C1.2).
//
// Ablauf: Datei-Auswahl (CSV/Hersteller-Tabelle) → Upload → der Server schlägt je Zeile eine
// Register→Tag-Bindung vor (niedrig-konfident) → der Ingenieur ordnet in der Tabelle Register↔Tag
// zu, übernimmt oder verwirft jede Zeile, korrigiert den Tag bei Bedarf → Bestätigen schreibt den
// Katalog (jede Variable mit modbus-Block, rein lesend). Kein Schreibpfad zur Anlage, kein
// Adressraum-Sweep: die Map wird importiert, nicht abgetastet.
//
// Bewusst schema-nah: die Tabelle zeigt genau die Felder, die die Bindung trägt (Register/Kind,
// Datentyp, Wortreihenfolge, Skalierung, Einheit) plus Tag, Konfidenz und die Hinweise aus der
// Ableitung. Nicht bindbare Zeilen sind markiert und können nur verworfen werden.

import { useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { confirmModbusMap, importModbusMap } from '../api/client';
import { ApiError } from '../api/client';
import type {
  ModbusMapDecision,
  ModbusMapProposal,
  ModbusProposalBinding,
} from '../api/types';
import { Button, PanelHeading } from '../components/ui';

export interface ModbusMappingDialogProps {
  // Das Zielprojekt, in dessen Katalog die bestätigte Zuordnung geschrieben wird.
  projectId: string;
  onClose: () => void;
  // Nach erfolgreicher Bestätigung: den Katalog neu laden (der Aufrufer aktualisiert seinen Zustand).
  onConfirmed: (rev: string, count: number) => void | Promise<void>;
}

// Register-Kurzform „Holding 40001" für die Tabelle.
function formatRegister(b: ModbusProposalBinding): string {
  const label: Record<string, string> = {
    coil: 'Coil',
    discrete_input: 'Discrete',
    holding: 'Holding',
    input: 'Input',
  };
  const kind = label[b.kind] ?? b.kind;
  const bit = b.bit !== null && b.bit !== undefined ? `.${b.bit}` : '';
  return `${kind} ${b.address}${bit}`;
}

function formatScaling(b: ModbusProposalBinding): string {
  if (b.scaling_factor === 1 && b.scaling_offset === 0) return '—';
  return `×${b.scaling_factor}${b.scaling_offset ? ` ${b.scaling_offset >= 0 ? '+' : ''}${b.scaling_offset}` : ''}`;
}

export function ModbusMappingDialog({
  projectId,
  onClose,
  onConfirmed,
}: ModbusMappingDialogProps): JSX.Element {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);

  const [proposals, setProposals] = useState<ModbusMapProposal[]>([]);
  const [confirmed, setConfirmed] = useState<Record<number, boolean>>({});
  const [tagEdits, setTagEdits] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState(false);

  const runImport = useCallback(
    async (file: File) => {
      setBusy(true);
      setError(null);
      try {
        const result = await importModbusMap(projectId, file);
        setProposals(result.proposals);
        // Startzustand: bindbare Zeilen als „übernehmen" vorschlagen, nicht bindbare verwerfen.
        // Die Bestätigung bleibt Pflicht — der Ingenieur prüft jede Zeile.
        const init: Record<number, boolean> = {};
        for (const p of result.proposals) init[p.line] = p.binding !== null;
        setConfirmed(init);
        setTagEdits({});
        setImported(true);
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
        );
      } finally {
        setBusy(false);
      }
    },
    [projectId],
  );

  const toggle = useCallback((line: number, value: boolean) => {
    setConfirmed((prev) => ({ ...prev, [line]: value }));
  }, []);

  const setAll = useCallback(
    (value: boolean) => {
      setConfirmed(() => {
        const next: Record<number, boolean> = {};
        for (const p of proposals) next[p.line] = value && p.binding !== null;
        return next;
      });
    },
    [proposals],
  );

  const confirmedCount = useMemo(
    () => proposals.filter((p) => confirmed[p.line]).length,
    [proposals, confirmed],
  );

  const runConfirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const decisions: ModbusMapDecision[] = proposals.map((p) => {
        const take = !!confirmed[p.line];
        const decision: ModbusMapDecision = { line: p.line, confirmed: take };
        const edited = tagEdits[p.line]?.trim();
        if (take && edited && edited !== p.tag) decision.tag = edited;
        return decision;
      });
      const result = await confirmModbusMap(projectId, proposals, decisions);
      await onConfirmed(result.rev, confirmedCount);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setBusy(false);
    }
  }, [proposals, confirmed, tagEdits, projectId, confirmedCount, onConfirmed]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('modbusMap.title')}
      data-testid="modbus-map-dialog"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col gap-4 overflow-y-auto rounded-lg border border-bf-border bg-bf-navy p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-bf-white">{t('modbusMap.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            data-testid="modbus-map-close"
            onClick={onClose}
            className="text-bf-muted hover:text-bf-white"
          >
            ✕
          </button>
        </div>

        <p className="text-xs text-bf-muted">{t('modbusMap.intro')}</p>

        {/* Rein lesend / kein Scan — die Kern-Leitplanke sichtbar. */}
        <div
          className="rounded border border-bf-gold/40 bg-bf-gold/5 p-2 text-xs text-bf-gold"
          data-testid="modbus-map-disclaimer"
        >
          {t('modbusMap.disclaimerTitle')}
        </div>

        {/* Datei-Auswahl. */}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,text/csv"
          className="hidden"
          data-testid="modbus-map-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void runImport(file);
          }}
        />

        {!imported && (
          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={busy}
              testId="modbus-map-choose"
              onClick={() => fileRef.current?.click()}
            >
              {busy ? t('modbusMap.importing') : t('modbusMap.choose')}
            </Button>
          </div>
        )}

        {imported && proposals.length === 0 && (
          <p className="text-sm text-bf-muted" data-testid="modbus-map-empty">
            {t('modbusMap.noProposals')}
          </p>
        )}

        {/* Mapping-Tabelle: Register ↔ Tag, Übernehmen/Verwerfen je Zeile. */}
        {proposals.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <PanelHeading>{t('modbusMap.tableCaption')}</PanelHeading>
              <div className="flex gap-2">
                <Button testId="modbus-map-select-all" onClick={() => setAll(true)}>
                  {t('modbusMap.selectAll')}
                </Button>
                <Button testId="modbus-map-discard-all" onClick={() => setAll(false)}>
                  {t('modbusMap.discardAll')}
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs" data-testid="modbus-map-table">
                <thead>
                  <tr className="border-b border-bf-border text-bf-muted">
                    <th className="px-2 py-1.5">{t('modbusMap.colDecision')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colTag')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colRegister')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colDatatype')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colWordOrder')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colScaling')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colUnit')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colConfidence')}</th>
                    <th className="px-2 py-1.5">{t('modbusMap.colIssues')}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => {
                    const take = !!confirmed[p.line];
                    const b = p.binding;
                    return (
                      <tr
                        key={p.line}
                        className={`border-b border-bf-border/50 ${take ? '' : 'opacity-50'}`}
                        data-testid={`modbus-map-row-${p.line}`}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={take}
                            disabled={b === null}
                            data-testid={`modbus-map-take-${p.line}`}
                            onChange={(e) => toggle(p.line, e.target.checked)}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            aria-label={t('modbusMap.editTag')}
                            value={tagEdits[p.line] ?? p.tag}
                            data-testid={`modbus-map-tag-${p.line}`}
                            onChange={(e) =>
                              setTagEdits((prev) => ({ ...prev, [p.line]: e.target.value }))
                            }
                            className="w-28 rounded border border-bf-border bg-bf-navy px-1.5 py-1 text-bf-white outline-none focus:border-bf-gold"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-bf-white">
                          {b ? formatRegister(b) : <span className="text-red-300">{t('modbusMap.notBindable')}</span>}
                        </td>
                        <td className="px-2 py-1.5 text-bf-muted">{b?.datatype ?? '—'}</td>
                        <td className="px-2 py-1.5 text-bf-muted">{b?.word_order ?? '—'}</td>
                        <td className="px-2 py-1.5 text-bf-muted">{b ? formatScaling(b) : '—'}</td>
                        <td className="px-2 py-1.5 text-bf-muted">{b?.unit ?? '—'}</td>
                        <td className="px-2 py-1.5 text-bf-muted">
                          {Math.round(p.confidence * 100)}%
                        </td>
                        <td className="px-2 py-1.5 text-amber-200" title={p.issues.join(' ')}>
                          {p.issues.length > 0 ? p.issues.join(' ') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-bf-muted" data-testid="modbus-map-review-note">
              {t('modbusMap.reviewNote')}
            </p>
          </>
        )}

        {error && (
          <p className="text-xs text-red-300" data-testid="modbus-map-error">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {imported && (
            <Button
              testId="modbus-map-choose-another"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {t('modbusMap.chooseAnother')}
            </Button>
          )}
          {proposals.length > 0 && (
            <Button
              variant="primary"
              testId="modbus-map-confirm"
              disabled={busy}
              onClick={() => void runConfirm()}
            >
              {busy ? t('modbusMap.confirmBusy') : t('modbusMap.confirm')}
            </Button>
          )}
          <Button testId="modbus-map-cancel" onClick={onClose}>
            {t('modbusMap.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
